/**
 * Aleatoriedad por contador (ADR 0006, doc. 04 §10.1).
 *
 * Cada número uniforme u ∈ (0, 1) es una función de mezcla (hash) de una clave:
 *   u = H(semillaMaestra, réplica, flujo, entidad, etapa, ocurrencia, variable)
 * El valor no depende del orden en que el motor lo pide, así que la misma OT recibe las mismas
 * «circunstancias» en todos los escenarios (números aleatorios comunes).
 *
 * Funciones de mezcla de dominio público: pasos de MurmurHash3 (32 bits) y su finalizador.
 */

export type ComponenteClave = string | number;

const C1 = 0xcc9e2d51;
const C2 = 0x1b873593;

/** Finalizador de MurmurHash3 (avalancha completa sobre 32 bits). */
export function mezclar32(h: number): number {
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** Un paso de MurmurHash3: incorpora la palabra de 32 bits `k` al estado `h`. */
function incorporar(h: number, k: number): number {
  k = Math.imul(k, C1);
  k = (k << 15) | (k >>> 17);
  k = Math.imul(k, C2);
  h ^= k;
  h = (h << 13) | (h >>> 19);
  h = (Math.imul(h, 5) + 0xe6546b64) | 0;
  return h >>> 0;
}

/** Hash de 32 bits de un texto (MurmurHash3 sobre las unidades UTF-16). */
export function hashTexto(texto: string, semilla = 0): number {
  let h = semilla >>> 0;
  for (let i = 0; i < texto.length; i++) h = incorporar(h, texto.charCodeAt(i));
  h ^= texto.length;
  return mezclar32(h);
}

const DOS_A_LA_32 = 4294967296;

/** Convierte un componente de la clave en dos palabras de 32 bits con una etiqueta de tipo. */
function palabras(c: ComponenteClave): [number, number, number] {
  if (typeof c === 'string') return [0x5354, hashTexto(c), c.length];
  if (!Number.isInteger(c)) throw new Error(`Componente de clave no entero: ${c}`);
  const negativo = c < 0 ? 1 : 0;
  const abs = Math.abs(c);
  const bajo = abs % DOS_A_LA_32;
  const alto = Math.floor(abs / DOS_A_LA_32);
  return [0x4e55 + negativo, bajo >>> 0, alto >>> 0];
}

function hashClave(clave: readonly ComponenteClave[], semilla: number): number {
  let h = semilla >>> 0;
  for (const c of clave) {
    const [etiqueta, a, b] = palabras(c);
    h = incorporar(h, etiqueta);
    h = incorporar(h, a);
    h = incorporar(h, b);
  }
  h ^= clave.length;
  return mezclar32(h);
}

const SEMILLA_A = 0x9e3779b9;
const SEMILLA_B = 0x7f4a7c15;
const DOS_A_LA_53 = 9007199254740992;

/**
 * Número uniforme en el intervalo abierto (0, 1) con 53 bits de resolución, función pura de la
 * clave. Combina dos hashes de 32 bits con semillas distintas.
 */
export function uniformeDeClave(clave: readonly ComponenteClave[]): number {
  const a = hashClave(clave, SEMILLA_A) >>> 5; // 27 bits
  const b = hashClave(clave, SEMILLA_B) >>> 6; // 26 bits
  return (a * 67108864 + b + 0.5) / DOS_A_LA_53;
}

/** Flujos aleatorios independientes (doc. 04 §10.1). */
export type Flujo = 'demanda' | 'unidad' | 'persona' | 'evento';

/**
 * Generador de una réplica: fija la semilla maestra y el número de réplica; el resto de la clave
 * la aporta quien pide el número.
 */
export interface GeneradorReplica {
  readonly semilla: number;
  readonly replica: number;
  u(flujo: Flujo, ...resto: ComponenteClave[]): number;
}

export function crearGenerador(semilla: number, replica: number): GeneradorReplica {
  return {
    semilla,
    replica,
    u: (flujo, ...resto) => uniformeDeClave([semilla, replica, flujo, ...resto]),
  };
}
